import { useEffect, useRef, useState } from 'react';

interface UseSectionExpansionProps {
  searchTerm: string;
  sectionExpanded: boolean;
  defaultExpanded?: boolean;
}

/**
 * Custom hook to manage section expansion state based on search results.
 * Auto-expands when search results first appear in a section and resets to default when search is cleared.
 */
export const useSectionExpansion = ({
  searchTerm,
  sectionExpanded,
  defaultExpanded = false,
}: UseSectionExpansionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const prevSectionExpansionRef = useRef(false);

  useEffect(() => {
    if (!searchTerm) {
      // Reset expansion when search is cleared
      setIsExpanded(defaultExpanded);
      prevSectionExpansionRef.current = false;
    } else if (sectionExpanded && !prevSectionExpansionRef.current) {
      // Only auto-expand when sectionExpansion changes from false to true
      setIsExpanded(true);
      prevSectionExpansionRef.current = true;
    } else if (!sectionExpanded) {
      prevSectionExpansionRef.current = false;
    }
  }, [searchTerm, sectionExpanded, defaultExpanded]);

  return { isExpanded, setIsExpanded };
};
